// Interactive satisfaction-survey demo. A miniature of the real survey
// response page (backend/signatures/survey_page.py): the same 3D mood mascot,
// the same stage tints and labels. Pick a low score and the
// negative-feedback alarm fires, as it does for real recipients.
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { api } from '../../../config/api';
import { EASE_OUT } from './primitives';
import { useT } from '../../../i18n';
import './SurveyDemo.css';

const BASE = [4, 6, 14, 41, 67];
const messages = {
  // Same 5-point scale and wording as SurveyTemplate.SCALE_EMOJIS and its
  // English labels (signatures/models.py).
  labels: {
    tr: ['Memnun Değil', 'Az Memnun', 'Nötr', 'Memnun', 'Çok Memnun'],
    en: ['Not Satisfied', 'Somewhat Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied'],
  },
  sender: { tr: 'Ayşe Yılmaz · Norda Lojistik', en: 'Ayşe Yılmaz · Norda Logistics' },
  question: { tr: 'Bu yazışmadan ne kadar memnun kaldınız?', en: 'How satisfied are you with this conversation?' },
  ratingAria: { tr: 'Memnuniyet puanı', en: 'Satisfaction rating' },
  rate: { tr: 'Puanla', en: 'Rate' },
  tapStar: { tr: 'Bir yıldıza dokunun', en: 'Tap a star' },
  alarmTitle: { tr: '⚠ Olumsuz geri bildirim alarmı', en: '⚠ Negative feedback alert' },
  alarmText: {
    tr: '{n}/5 puan verildi. Belirlediğiniz adrese anında e-posta gitti, panelde takip aksiyonu açıldı.',
    en: 'Rated {n}/5. An email went straight to the address you chose, and a follow-up action opened in the panel.',
  },
  okTitle: { tr: 'Yanıt kaydedildi', en: 'Response recorded' },
  okText: {
    tr: '{n}/5 puan, Satış · Türkçe şablonunun memnuniyet raporuna işlendi.',
    en: 'Rated {n}/5 and added to the Sales · English template’s satisfaction report.',
  },
  distAria: { tr: 'Örnek puan dağılımı', en: 'Sample score distribution' },
};
const FACES = ['😞', '😕', '😐', '😊', '😍'];
const IDLE_MOOD = 0.75; // the response page's default before a rating

const moodOf = (r) => (r ? (r - 1) / 4 : IDLE_MOOD);

// The mascot bundle (tools/survey-mascot → three.js) is the one the backend
// already serves to the survey page. Loaded at runtime, only when the
// section gets close, so it costs the landing page nothing up front.
function Mascot({ rating, celebrate }) {
  const boxRef = useRef(null);
  const inst = useRef(null);
  const moodRef = useRef(moodOf(rating));
  const near = useInView(boxRef, { once: true, margin: '600px 0px' });
  const reduce = useReducedMotion();
  const [ready, setReady] = useState(false);

  moodRef.current = moodOf(rating);

  useEffect(() => {
    if (!near || !boxRef.current) return undefined;
    let disposed = false;
    const canvas = document.createElement('canvas');
    canvas.className = 'lp-sv-mascot-canvas';
    boxRef.current.prepend(canvas);
    const url = `${api.defaults.baseURL}/api/signatures/surveys/assets/mascot.js`;
    import(/* webpackIgnore: true */ url)
      .then((mod) => {
        if (disposed) return;
        const m = mod.createMascot(canvas, { initialMood: moodRef.current, reducedMotion: !!reduce });
        inst.current = m;
        Promise.resolve(m.ready).then(() => { if (!disposed) setReady(true); });
      })
      .catch(() => { /* no WebGL / offline backend: the emoji fallback stays */ });
    return () => {
      disposed = true;
      if (inst.current) inst.current.dispose();
      inst.current = null;
      canvas.remove();
    };
  }, [near, reduce]);

  useEffect(() => {
    if (inst.current) inst.current.setMood(moodOf(rating));
  }, [rating]);

  useEffect(() => {
    if (celebrate && inst.current) inst.current.celebrate(moodOf(celebrate.rating));
  }, [celebrate]);

  return (
    <div ref={boxRef} className={`lp-sv-mascot ${ready ? 'is-ready' : ''}`}>
      <span className="lp-sv-mascot-fallback" aria-hidden="true">{FACES[(rating || 4) - 1]}</span>
    </div>
  );
}

export function SurveyDemo() {
  const [hover, setHover] = useState(0);
  const [picked, setPicked] = useState(0);
  const [celebrate, setCelebrate] = useState(null);
  const { t } = useT(messages);
  const labels = t('labels');
  const shown = hover || picked;
  const dist = BASE.map((v, i) => v + (picked === i + 1 ? 1 : 0));
  const total = dist.reduce((a, b) => a + b, 0);

  const pick = (n) => {
    setPicked(n);
    setCelebrate({ rating: n, at: Date.now() });
  };

  return (
    <div className="lp-sv">
      <div className={`lp-sv-page mood-${shown || 'idle'}`}>
        <div className="lp-sv-page-head">
          <span className="lp-sv-mail-dot" />
          <span>{t('sender')}</span>
        </div>
        <p className="lp-sv-q">{t('question')}</p>
        <Mascot rating={shown} celebrate={celebrate} />
        <div className="lp-sv-stars" onPointerLeave={() => setHover(0)} role="radiogroup" aria-label={t('ratingAria')}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={picked === n}
              aria-label={`${n} — ${labels[n - 1]}`}
              className={`lp-sv-star ${n <= shown ? 'is-on' : ''}`}
              onPointerEnter={() => setHover(n)}
              onFocus={() => setHover(n)}
              onBlur={() => setHover(0)}
              onClick={() => pick(n)}
              data-cursor={t('rate')}
            >
              ★
            </button>
          ))}
        </div>
        <span className="lp-sv-label">{shown ? labels[shown - 1] : t('tapStar')}</span>
      </div>

      <AnimatePresence mode="wait">
        {picked > 0 && (
          <motion.div
            key={picked <= 2 ? 'alarm' : 'ok'}
            className={`lp-sv-result ${picked <= 2 ? 'is-alarm' : 'is-ok'}`}
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: EASE_OUT }}
          >
            {picked <= 2 ? (
              <>
                <strong>{t('alarmTitle')}</strong>
                <span>{t('alarmText', { n: picked })}</span>
              </>
            ) : (
              <>
                <strong><span className="lp-tick lp-sv-ok-tick" aria-hidden="true" />{t('okTitle')}</strong>
                <span>{t('okText', { n: picked })}</span>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lp-sv-dist" aria-label={t('distAria')}>
        {dist.map((v, i) => (
          <div key={i} className={`lp-sv-dist-row ${picked === i + 1 ? 'is-you' : ''}`}>
            <span>{i + 1}★</span>
            <div className="lp-sv-dist-track">
              <motion.div className="lp-sv-dist-bar" animate={{ width: `${(v / total) * 100}%` }} transition={{ duration: 0.7, ease: EASE_OUT }} />
            </div>
            <span className="lp-sv-dist-n">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SurveyDemo;
